import React, { useRef, useState } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter } from '@ui/molecules/Modal';
import { Button } from '@ui/atoms/Button';
import { Text } from '@ui/atoms/Text';
const ACCEPTED_FILE_TYPES = '.md,.txt';
const ACCEPTED_EXTENSIONS = ACCEPTED_FILE_TYPES.split(',');
function hasAcceptedExtension(file) {
    const name = file.name.toLowerCase();
    return ACCEPTED_EXTENSIONS.some((ext) => name.endsWith(ext));
}
/**
 * IP-14859/IP-14877 — Add Document modal (QUI). A modal, not a drawer.
 * Multi-file picker: click-to-browse (hidden file input) or drag-and-drop
 * onto the dropzone. Selected files are handed to the parent on Upload,
 * which routes them into the AI chat widget's ingest flow.
 */
export default function AddDocumentModal({ isOpen, onClose, onUpload, t }) {
    const [selectedFiles, setSelectedFiles] = useState([]);
    const [isDragActive, setDragActive] = useState(false);
    const [hasRejectedFiles, setHasRejectedFiles] = useState(false);
    const fileInputRef = useRef(null);
    const resetFiles = () => {
        setSelectedFiles([]);
        setHasRejectedFiles(false);
    };
    const handleClose = () => {
        resetFiles();
        onClose();
    };
    const openFilePicker = () => { var _a; return (_a = fileInputRef.current) === null || _a === void 0 ? void 0 : _a.click(); };
    const handleFileInputChange = (e) => {
        if (e.target.files) {
            setSelectedFiles(Array.from(e.target.files));
        }
        // Allow re-selecting the same file(s) after a reset.
        e.target.value = '';
    };
    const handleDrop = (e) => {
        e.preventDefault();
        setDragActive(false);
        if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
            const dropped = Array.from(e.dataTransfer.files);
            const accepted = dropped.filter(hasAcceptedExtension);
            setSelectedFiles(accepted);
            setHasRejectedFiles(accepted.length < dropped.length);
        }
    };
    const handleDragOver = (e) => {
        e.preventDefault();
        setDragActive(true);
    };
    const handleDragLeave = (e) => {
        e.preventDefault();
        setDragActive(false);
    };
    const handleUpload = () => {
        onUpload(selectedFiles);
        resetFiles();
    };
    return (React.createElement(Modal, { isOpen: isOpen, onClose: handleClose, ariaLabel: t('documents.add_modal.title') },
        React.createElement(ModalHeader, { title: t('documents.add_modal.title') }),
        React.createElement(ModalBody, null,
            React.createElement(Text, { variant: "body", as: "p", style: { margin: '0 0 20px' } }, t('documents.add_modal.intro')),
            React.createElement("input", { ref: fileInputRef, type: "file", accept: ACCEPTED_FILE_TYPES, multiple: true, hidden: true, "data-testid": "tkb-doc-file-input", onChange: handleFileInputChange }),
            React.createElement("div", { className: `tkb-doc-dropzone${isDragActive ? ' tkb-doc-dropzone--active' : ''}`, "data-testid": "tkb-doc-dropzone", role: "button", tabIndex: 0, onClick: openFilePicker, onKeyDown: (e) => {
                    if (e.key === 'Enter' || e.key === ' ')
                        openFilePicker();
                }, onDrop: handleDrop, onDragOver: handleDragOver, onDragLeave: handleDragLeave },
                React.createElement("i", { className: "fa-light fa-cloud-arrow-up tkb-doc-dropzone__icon", "aria-hidden": "true" }),
                React.createElement(Text, { variant: "body", as: "p", className: "tkb-doc-dropzone__prompt" },
                    React.createElement("span", { className: "tkb-doc-dropzone__link" }, t('documents.add_modal.select_files')),
                    ' ',
                    t('documents.add_modal.select_files_suffix'),
                    React.createElement("br", null),
                    t('documents.add_modal.drag_drop')),
                React.createElement(Text, { variant: "small", as: "p", className: "tkb-doc-dropzone__hint" },
                    t('documents.add_modal.accepted_types'),
                    React.createElement("br", null),
                    t('documents.add_modal.max_size')),
                hasRejectedFiles && (React.createElement(Text, { variant: "small", as: "p", className: "tkb-doc-dropzone__error", "data-testid": "tkb-doc-rejected-files" }, t('documents.add_modal.unsupported_type'))),
                selectedFiles.length > 0 && (React.createElement("ul", { className: "tkb-doc-dropzone__files", "data-testid": "tkb-doc-selected-files" }, selectedFiles.map((file) => (React.createElement("li", { key: file.name, className: "tkb-doc-dropzone__file" }, file.name))))))),
        React.createElement(ModalFooter, null,
            React.createElement(Button, { variant: "secondary", onClick: handleClose, style: { marginRight: '8px' } }, t('documents.add_modal.cancel')),
            React.createElement(Button, { variant: "primary", onClick: handleUpload, disabled: selectedFiles.length === 0 }, t('documents.add_modal.upload')))));
}
